import {
  type RepoIssue,
  RepoIssuePullRequestDisplayState,
  RepoIssuePullRequestState,
  type RepoIssuesDashboardResult,
  RepoIssueState,
  RepoIssueStateReason,
  type RepoIssueStatus,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import type {EnrichedIssueListItem, IssueItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {
  DisplayStateType,
  type EnrichedPullListItem,
  type PullItem,
  StateType,
} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'

type IssueFieldValue = NonNullable<IssueItem['issueFieldValues']>[number]
type RepoIssueListItem = EnrichedIssueListItem & {id: number | string}
type RepoPullListItem = EnrichedPullListItem & {itemType: 'pull_request'; id: number | string}

function issueState(state: RepoIssueState): IssueItem['state'] {
  return state === RepoIssueState.CLOSED ? 'CLOSED' : 'OPEN'
}

function issueStateReason(stateReason: RepoIssueStateReason): IssueItem['stateReason'] {
  switch (stateReason) {
    case RepoIssueStateReason.COMPLETED:
      return 'COMPLETED'
    case RepoIssueStateReason.NOT_PLANNED:
      return 'NOT_PLANNED'
    case RepoIssueStateReason.REOPENED:
      return 'REOPENED'
    case RepoIssueStateReason.DUPLICATE:
      return 'DUPLICATE'
    default:
      return null
  }
}

function issueStatus(status: RepoIssueStatus | undefined): IssueItem['status'] {
  if (!status) return undefined
  return {name: status.name, icon: status.icon, color: status.color, category: status.category}
}

const ISSUE_FIELD_DATA_TYPES = new Set<IssueFieldValue['dataType']>([
  'text',
  'single_select',
  'multi_select',
  'date',
  'number',
])

function issueFieldValue(fieldValue: RepoIssue['issueFieldValues'][number]): IssueFieldValue | undefined {
  if (!ISSUE_FIELD_DATA_TYPES.has(fieldValue.dataType as IssueFieldValue['dataType'])) return undefined
  return {
    fieldName: fieldValue.fieldName,
    dataType: fieldValue.dataType as IssueFieldValue['dataType'],
    value: fieldValue.value,
    numberValue: fieldValue.numberValue,
    options: fieldValue.options,
  }
}

function issueToListItem(issue: RepoIssue, id: string): RepoIssueListItem {
  return {
    itemType: 'issue',
    id,
    globalId: id || undefined,
    number: issue.number,
    title: issue.title,
    titleHtml: issue.titleHtml,
    state: issueState(issue.state),
    stateReason: issueStateReason(issue.stateReason),
    status: issueStatus(issue.status),
    closed: issue.closed,
    closedAt: issue.closedAt ?? null,
    createdAt: issue.createdAt,
    updatedAt: issue.updatedAt,
    commentCount: issue.commentCount,
    permalink: issue.permalink ?? '',
    repoNameWithOwner: issue.repoNameWithOwner ?? '',
    labels: issue.labels,
    assignees: issue.assignees,
    author: {
      login: issue.author?.login ?? '',
      name: issue.author?.name,
      avatarUrl: issue.author?.avatarUrl ?? '',
      resourcePath: issue.author?.resourcePath ?? '',
      typename: issue.author?.typename ?? '',
      isCopilot: issue.author?.isCopilot ?? false,
      isAgent: issue.author?.isAgent ?? false,
    },
    issueType: issue.issueType ?? null,
    milestone: issue.milestone ?? null,
    issueFieldValues: issue.issueFieldValues.flatMap(fieldValue => {
      const value = issueFieldValue(fieldValue)
      return value ? [value] : []
    }),
    aliveChannel: issue.aliveChannel,
  }
}

function pullState(state: RepoIssuePullRequestState): PullItem['state'] {
  if (state === RepoIssuePullRequestState.CLOSED) return StateType.Closed
  if (state === RepoIssuePullRequestState.MERGED) return StateType.Merged
  return StateType.Opened
}

function pullDisplayState(state: RepoIssuePullRequestDisplayState): PullItem['displayState'] {
  switch (state) {
    case RepoIssuePullRequestDisplayState.DRAFT:
      return DisplayStateType.Draft
    case RepoIssuePullRequestDisplayState.OPEN:
      return DisplayStateType.Opened
    case RepoIssuePullRequestDisplayState.CLOSED:
      return DisplayStateType.Closed
    case RepoIssuePullRequestDisplayState.MERGED:
      return DisplayStateType.Merged
    case RepoIssuePullRequestDisplayState.QUEUED:
      return DisplayStateType.Queued
    default:
      return undefined
  }
}

function pullToListItem(pull: RepoIssuesDashboardResult['item'] & {case: 'pullRequest'}, id: string): RepoPullListItem {
  const value = pull.value
  return {
    itemType: 'pull_request',
    id,
    globalId: id || undefined,
    number: value.number,
    title: value.title,
    titleHtml: value.titleHtml,
    state: pullState(value.state),
    displayState: pullDisplayState(value.displayState),
    isDraft: value.isDraft,
    isReadByCurrentUser: value.isReadByCurrentUser,
    permalink: value.permalink ?? '',
    repoNameWithOwner: value.repoNameWithOwner ?? '',
    labels: value.labels,
    assignees: value.assignees,
    author: {displayLogin: value.author?.displayLogin ?? ''},
    agentAttributedUser: value.agentAttributedUser,
    commentCount: value.commentCount,
    commitHeadShaChannel: value.commitHeadShaChannel,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    closedAt: value.closedAt,
    mergedAt: value.mergedAt,
    headSha: value.headSha,
    stackPosition: value.stackPosition ?? null,
    stackSize: value.stackSize ?? null,
    aliveChannel: value.aliveChannel,
  }
}

export function repoIssuesToListItems(
  results: RepoIssuesDashboardResult[],
): Array<RepoIssueListItem | RepoPullListItem> {
  const items: Array<RepoIssueListItem | RepoPullListItem> = []
  for (const result of results) {
    if (result.item.case === 'issue') items.push(issueToListItem(result.item.value, result.id))
    if (result.item.case === 'pullRequest') items.push(pullToListItem(result.item, result.id))
  }
  return items
}
